import { Stack } from "@mui/material";
import type { FC } from "react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/usePortfolioDocumentsQuery";
import { usePortfolioIdReportingQuery } from "web_myplann/Api/Portfolio/usePortfolioIdReportingQuery";
import { ClientPortfolioPaymentFrequencyEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DataLineChart } from "web_myplann/Portfolio/Components/DataLineChart/DataLineChart";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import { BasicInfo } from "web_myplann/Portfolio/Components/PortfolioSections/BasicInfo";
import { InvestmentTool } from "web_myplann/Portfolio/Components/PortfolioTools/InvestmentTool/InvestmentTool";
import { RelatedSection } from "web_myplann/Portfolio/Components/RelatedSection";
import { SectionDivider } from "web_myplann/Portfolio/Components/SectionDidiver";
import { calculateGraphData } from "web_myplann/Portfolio/Helpers/calculateGraphData";
import { computeRelatedPresence } from "web_myplann/Portfolio/Helpers/getHasRelated";
import { getPaidAllLogic } from "web_myplann/Portfolio/Helpers/getPaidAllLogic";
import {
	getPortfolioFrequencyValue,
	getPortfolioValueDate,
} from "web_myplann/Portfolio/Helpers/utils";
import {
	DataCard,
	DataCardPreset,
} from "web_myplann/Shared/Components/DataCard";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import { moneyFormatter } from "web_myplann/Shared/Utils/formatters";
import type { PortfolioDetailProps } from "web_myplann/Utils/types";

const PageResources = Resources.PortfolioDetail;

export const Investment: FC<PortfolioDetailProps> = ({
	baseData,
	currencySign,
}) => {
	const { t } = useResource();

	const { data: documents, isLoading: isDocumentsLoading } =
		usePortfolioDocumentsQuery(baseData.id);
	const { data: reporting, isLoading: isReportingLoading } =
		usePortfolioIdReportingQuery(baseData.id);

	if (isDocumentsLoading || isReportingLoading) {
		return <DetailSectionLoader />;
	}

	const { daysData, totalValue, totalInvestedValue, toDate, showChartInfo } =
		calculateGraphData(
			reporting?.reportingData,
			reporting?.summary ?? undefined,
		);

	const { paidAllValue, shouldShowPaidAll } = getPaidAllLogic({ baseData });

	const paidAll = getPortfolioFrequencyValue(
		paidAllValue,
		t(Resources.PortfolioDetail.SharedLabels.Currency[baseData.currency]),
		ClientPortfolioPaymentFrequencyEnum.Monthly,
		t,
		0,
	);

	return (
		<Stack>
			<DataLineChart
				hideDivider
				totalValue={totalValue}
				totalInvestedValue={totalInvestedValue}
				totalEvaluationPercent={reporting?.summary?.totalEvaluationPercent ?? 0}
				daysData={daysData}
				currencySign={currencySign}
				toDate={toDate}
				showChartInfo={showChartInfo}
			/>

			{showChartInfo && (
				<>
					<DataRow
						label={t(Resources.Portfolio.Chart.InvestedTogether)}
						value={moneyFormatter(totalInvestedValue, currencySign, "", 2)}
					/>

					<DataRow
						label={t(Resources.Common.ToDate)}
						value={getPortfolioValueDate(daysData, baseData)}
					/>
				</>
			)}
			{daysData.length !== 0 && <SectionDivider />}

			<DataCard
				preset={DataCardPreset.BasicInfo}
				bottomComponent={<BasicInfo data={baseData} />}
				noDivider={
					!computeRelatedPresence(
						documents?.data,
						baseData.related,
						baseData.tools,
					).hasAny && baseData.tools.length === 0
				}
			>
				<DataRow
					label={t(PageResources.SharedLabels.name)}
					value={baseData.clientName}
				/>
				<DataRow
					label={t(PageResources.SharedLabels.contractNumber)}
					value={baseData.contractNumber}
				/>
				<DataRow
					label={t(PageResources.SharedLabels.producent)}
					value={baseData.producer}
				/>
				<DataRow
					label={t(PageResources.SharedLabels.product)}
					value={baseData.product}
				/>
				{shouldShowPaidAll && (
					<DataRow
						label={t(PageResources.SharedLabels.paidAll)}
						value={paidAll}
					/>
				)}
			</DataCard>

			<InvestmentTool
				tools={baseData.tools}
				producerInstruments={baseData.producerInstruments}
				toolsSummary={reporting?.toolsSummary}
				portfolioId={baseData.id}
				title={t(PageResources.Investment.InvestmentTool.title)}
				noDivider={
					!computeRelatedPresence(documents?.data, baseData.related).hasAny
				}
			/>

			<RelatedSection
				documents={documents?.data}
				related={baseData.related}
				portfolioId={baseData.id}
			/>
		</Stack>
	);
};
